<script lang="ts">
  /**
   * Inställningar-vy (S-04-22b + S-04-22-frontend) — sektion-baserad
   * inställnings-yta som öppnas från kugghjul-ikon i headern. Sektionerna
   * är utbyggbara — nya inställningsområden (notifiering, pilot-konfig)
   * läggs till som nya `Section`-fall utan refaktor av shellen.
   *
   * Karta-sektionen är interaktiv — admin kan ändra default-zoom och
   * center-koordinater för Lyktans karta. Värdena persisteras i
   * `app_settings`-tabellen via `PUT /api/admin/settings/map.default`
   * och varje ändring audit-loggas i `admin_actions` med gamla + nya
   * värden (S-04-22a backend).
   *
   * Tab-slug `settings` återanvänds — MFA-banner-flödet
   * (`goToMfaSetup` → activeTab = 'settings' + öppna MfaSetupView)
   * fungerar oförändrat.
   */
  import { onMount } from 'svelte';
  import {
    listSettings,
    parseMapDefault,
    parseWalkerAppLatest,
    parseWalkerAppMinSupported,
    saveMapDefault,
    saveWalkerAppLatest,
    saveWalkerAppMinSupported,
    describeSaveError,
    describeWalkerAppLatestError,
    describeWalkerAppMinSupportedError,
    type MapDefault,
    type WalkerAppLatest,
    type WalkerAppMinSupported,
  } from './appSettingsClient';

  type Section = 'konto' | 'sakerhet' | 'karta' | 'walker_app';

  import FrejaBindSection from '../auth/FrejaBindSection.svelte';

  type Props = {
    /** Anropas när admin klickar på "Aktivera/Sätt om tvåfaktor". */
    onOpenMfaSetup: () => void;
    /** Sant om admin aktiverat MFA i pågående session — påverkar kopian. */
    mfaActivatedThisSession: boolean;
    /**
     * Backend-bekräftad MFA-status från GET /api/me (S-04-30).
     * - true: backend rapporterar aktivt TOTP
     * - false: backend rapporterar ingen MFA
     * - null: status ofetched eller fetch failade
     */
    mfaActivatedAtServer?: boolean | null;
    /** Anropas vid 401/403 så App-shellen kan tvinga ut admin till login. */
    onUnauthorized?: () => void;
  };
  const {
    onOpenMfaSetup,
    mfaActivatedThisSession,
    mfaActivatedAtServer = null,
    onUnauthorized,
  }: Props = $props();

  // Kombinerad state: aktiv om backend bekräftat ELLER aktiverat denna
  // session. Default-conservative: vid null (ofetched/fail) visar vi
  // 'aktivera'-flödet (samma som tidigare beteende).
  const mfaIsActive: boolean = $derived(mfaActivatedAtServer === true || mfaActivatedThisSession);

  let activeSection: Section = $state('konto');

  // Karta-sektion: state. Hardkodade defaults används som fallback om
  // backend ännu inte har en rad för `map.default` (initial pilot-state).
  const FALLBACK_MAP_DEFAULT: MapDefault = { zoom: 13, lat: 57.738, lng: 11.948 };
  let mapDefault: MapDefault = $state({ ...FALLBACK_MAP_DEFAULT });
  let mapDefaultLoading = $state(false);
  let mapLoadError: string | null = $state(null);

  // Form-state — separat från `mapDefault` så att admin kan redigera
  // utan att tappa de senast sparade värdena om de avbryter.
  let formZoom: string = $state(String(FALLBACK_MAP_DEFAULT.zoom));
  let formLat: string = $state(String(FALLBACK_MAP_DEFAULT.lat));
  let formLng: string = $state(String(FALLBACK_MAP_DEFAULT.lng));
  let saveError: string | null = $state(null);
  let saveSuccess = $state(false);
  let isSaving = $state(false);

  function applyMapDefault(value: MapDefault): void {
    mapDefault = value;
    formZoom = String(value.zoom);
    formLat = String(value.lat);
    formLng = String(value.lng);
  }

  onMount(() => {
    // Hämta alla settings i en request — laddar map.default + walker-app
    // version-konfig i ett anrop. listSettings returnerar all keys admin
    // har rätt att läsa.
    void loadAllSettings();
  });

  function selectSection(section: Section): void {
    activeSection = section;
    if (section === 'karta') {
      saveError = null;
      saveSuccess = false;
    }
    if (section === 'walker_app') {
      minSaveError = null;
      minSaveSuccess = false;
      latestSaveError = null;
      latestSaveSuccess = false;
    }
  }

  async function handleSaveMapDefault(): Promise<void> {
    saveError = null;
    saveSuccess = false;

    const zoom = Number(formZoom);
    const lat = Number(formLat);
    const lng = Number(formLng);
    if (!Number.isFinite(zoom) || !Number.isFinite(lat) || !Number.isFinite(lng)) {
      saveError = 'Värdena måste vara numeriska.';
      return;
    }

    isSaving = true;
    const result = await saveMapDefault({ zoom, lat, lng });
    isSaving = false;

    if (result.kind === 'ok') {
      const parsed = parseMapDefault(result.item.value);
      if (parsed) applyMapDefault(parsed);
      saveSuccess = true;
      return;
    }
    if (result.kind === 'unauthorized') {
      onUnauthorized?.();
      return;
    }
    saveError = describeSaveError(result);
  }

  function handleResetToFallback(): void {
    formZoom = String(FALLBACK_MAP_DEFAULT.zoom);
    formLat = String(FALLBACK_MAP_DEFAULT.lat);
    formLng = String(FALLBACK_MAP_DEFAULT.lng);
    saveError = null;
    saveSuccess = false;
  }

  // ---------------------------------------------------------------------
  // S-04-23: Walker-app version-konfig
  // ---------------------------------------------------------------------
  // Två separata formulär — "Sätt minsta-version" (gating av äldre
  // appar) och "Sätt senaste-version" (download-URL för
  // 'Uppdatera Härden'-modalen i walker-appen). Backend-default vid
  // saknad rad: versionCode=1, versionName='0.1.0' (S-02-11).

  let minSupported: WalkerAppMinSupported = $state({ versionCode: 1, versionName: '0.1.0' });
  let latest: WalkerAppLatest = $state({
    versionCode: 1,
    versionName: '0.1.0',
    downloadUrl: 'https://harden.compuna.se/apk/harden-walker-0.1.0.apk',
  });

  // Form-state — separata strängar så admin kan skriva fritt utan att
  // förlora värden om hen avbryter.
  let formMinVersionCode: string = $state('1');
  let formMinVersionName: string = $state('0.1.0');
  let formLatestVersionCode: string = $state('1');
  let formLatestVersionName: string = $state('0.1.0');
  let formLatestDownloadUrl: string = $state('https://harden.compuna.se/apk/harden-walker-0.1.0.apk');

  let minSaveError: string | null = $state(null);
  let minSaveSuccess = $state(false);
  let isSavingMin = $state(false);
  let latestSaveError: string | null = $state(null);
  let latestSaveSuccess = $state(false);
  let isSavingLatest = $state(false);

  function applyMinSupported(value: WalkerAppMinSupported): void {
    minSupported = value;
    formMinVersionCode = String(value.versionCode);
    formMinVersionName = value.versionName;
  }

  function applyLatest(value: WalkerAppLatest): void {
    latest = value;
    formLatestVersionCode = String(value.versionCode);
    formLatestVersionName = value.versionName;
    formLatestDownloadUrl = value.downloadUrl;
  }

  // Single-fetch som laddar både map.default och walker_app-keys
  // i en query. Tidigare hade vi en separat `loadMapDefault` men efter
  // S-04-23-utvidgningen är konsoliderad load enklare och billigare.
  async function loadAllSettings(): Promise<void> {
    mapDefaultLoading = true;
    mapLoadError = null;
    const result = await listSettings();
    mapDefaultLoading = false;
    if (result.kind === 'unauthorized') {
      onUnauthorized?.();
      return;
    }
    if (result.kind === 'forbidden') {
      mapLoadError = 'Saknar behörighet att läsa inställningar.';
      return;
    }
    if (result.kind === 'error') {
      mapLoadError = result.message;
      return;
    }
    const mapItem = result.items.find((it) => it.key === 'map.default');
    if (mapItem) {
      const parsed = parseMapDefault(mapItem.value);
      if (parsed) applyMapDefault(parsed);
    }
    const minItem = result.items.find((it) => it.key === 'walker_app.min_supported');
    if (minItem) {
      const parsed = parseWalkerAppMinSupported(minItem.value);
      if (parsed) applyMinSupported(parsed);
    }
    const latestItem = result.items.find((it) => it.key === 'walker_app.latest');
    if (latestItem) {
      const parsed = parseWalkerAppLatest(latestItem.value);
      if (parsed) applyLatest(parsed);
    }
  }

  async function handleSaveMinSupported(): Promise<void> {
    minSaveError = null;
    minSaveSuccess = false;
    const versionCode = Number(formMinVersionCode);
    if (!Number.isInteger(versionCode) || versionCode < 1) {
      minSaveError = 'Version-kod måste vara ett heltal >= 1.';
      return;
    }
    isSavingMin = true;
    const result = await saveWalkerAppMinSupported({
      versionCode,
      versionName: formMinVersionName.trim(),
    });
    isSavingMin = false;
    if (result.kind === 'ok') {
      const parsed = parseWalkerAppMinSupported(result.item.value);
      if (parsed) applyMinSupported(parsed);
      minSaveSuccess = true;
      return;
    }
    if (result.kind === 'unauthorized') {
      onUnauthorized?.();
      return;
    }
    minSaveError = describeWalkerAppMinSupportedError(result);
  }

  async function handleSaveLatest(): Promise<void> {
    latestSaveError = null;
    latestSaveSuccess = false;
    const versionCode = Number(formLatestVersionCode);
    if (!Number.isInteger(versionCode) || versionCode < 1) {
      latestSaveError = 'Version-kod måste vara ett heltal >= 1.';
      return;
    }
    isSavingLatest = true;
    const result = await saveWalkerAppLatest({
      versionCode,
      versionName: formLatestVersionName.trim(),
      downloadUrl: formLatestDownloadUrl.trim(),
    });
    isSavingLatest = false;
    if (result.kind === 'ok') {
      const parsed = parseWalkerAppLatest(result.item.value);
      if (parsed) applyLatest(parsed);
      latestSaveSuccess = true;
      return;
    }
    if (result.kind === 'unauthorized') {
      onUnauthorized?.();
      return;
    }
    latestSaveError = describeWalkerAppLatestError(result);
  }
</script>

<section class="settings-section" aria-label="Inställningar">
  <div class="settings-layout">
    <nav class="section-nav" aria-label="Inställnings-sektioner">
      <button
        type="button"
        class="section-btn"
        class:active={activeSection === 'konto'}
        aria-current={activeSection === 'konto' ? 'page' : undefined}
        onclick={() => selectSection('konto')}
      >
        Konto
      </button>
      <button
        type="button"
        class="section-btn"
        class:active={activeSection === 'sakerhet'}
        aria-current={activeSection === 'sakerhet' ? 'page' : undefined}
        onclick={() => selectSection('sakerhet')}
      >
        Säkerhet
      </button>
      <button
        type="button"
        class="section-btn"
        class:active={activeSection === 'karta'}
        aria-current={activeSection === 'karta' ? 'page' : undefined}
        onclick={() => selectSection('karta')}
      >
        Karta
      </button>
      <button
        type="button"
        class="section-btn"
        class:active={activeSection === 'walker_app'}
        aria-current={activeSection === 'walker_app' ? 'page' : undefined}
        onclick={() => selectSection('walker_app')}
      >
        Härden-appen
      </button>
    </nav>

    <div class="section-content">
      {#if activeSection === 'konto'}
        <div class="settings-card">
          <h3>Konto</h3>
          <p>
            Egen profil och lösenordshantering tillkommer i en kommande iteration. I MVP1 hanteras
            kontoändringar via administratörsinbjudan.
          </p>
        </div>
      {:else if activeSection === 'sakerhet'}
        <div class="settings-card">
          <h3>Tvåfaktor (TOTP)</h3>
          <p>
            {#if mfaActivatedThisSession}
              Tvåfaktor aktiverades nyss för detta konto.
            {:else if mfaIsActive}
              Tvåfaktor är aktivt för detta konto. Du kan sätta upp ett nytt secret om du behöver
              byta authenticator-app.
            {:else}
              Aktivera tvåfaktor för att kunna hantera användarkonton och inaktivera konton.
            {/if}
          </p>
          <button type="button" class="settings-primary" onclick={onOpenMfaSetup}>
            {mfaIsActive ? 'Sätt upp tvåfaktor på nytt' : 'Aktivera tvåfaktor'}
          </button>
        </div>
        <FrejaBindSection />
      {:else if activeSection === 'karta'}
        <div class="settings-card">
          <h3>Karta</h3>
          <p>
            Sätter standardvärden för Lyktans karta — vad alla admins ser när de öppnar ritvyn. Nya
            värden sparas direkt i plattformen och syns nästa gång kartan laddas. Varje ändring
            audit-loggas.
          </p>

          {#if mapLoadError}
            <div class="alert error" role="alert">{mapLoadError}</div>
          {/if}

          <form
            class="map-form"
            onsubmit={(e) => {
              e.preventDefault();
              void handleSaveMapDefault();
            }}
          >
            <div class="field-row">
              <label class="field">
                <span>Zoom</span>
                <input
                  type="number"
                  step="0.5"
                  min="0"
                  max="22"
                  bind:value={formZoom}
                  required
                  disabled={isSaving || mapDefaultLoading}
                />
              </label>
              <label class="field">
                <span>Latitud</span>
                <input
                  type="number"
                  step="0.0001"
                  min="-90"
                  max="90"
                  bind:value={formLat}
                  required
                  disabled={isSaving || mapDefaultLoading}
                />
              </label>
              <label class="field">
                <span>Longitud</span>
                <input
                  type="number"
                  step="0.0001"
                  min="-180"
                  max="180"
                  bind:value={formLng}
                  required
                  disabled={isSaving || mapDefaultLoading}
                />
              </label>
            </div>

            {#if saveError}
              <div class="alert error" role="alert">{saveError}</div>
            {/if}
            {#if saveSuccess}
              <div class="alert success" role="status">
                Sparat. Nya värden gäller nästa gång kartan laddas.
              </div>
            {/if}

            <div class="form-actions">
              <button
                type="submit"
                class="settings-primary"
                disabled={isSaving || mapDefaultLoading}
              >
                {isSaving ? 'Sparar…' : 'Spara'}
              </button>
              <button
                type="button"
                class="settings-secondary"
                onclick={handleResetToFallback}
                disabled={isSaving}
              >
                Återställ till Hisingsbacka centrum
              </button>
            </div>
          </form>

          <p class="dim small">
            Senast sparat: zoom {mapDefault.zoom}, latitud {mapDefault.lat}, longitud {mapDefault.lng}.
          </p>
        </div>
      {:else if activeSection === 'walker_app'}
        <div class="settings-card">
          <h3>Härden-appen</h3>
          <p>
            Styr version-gate och uppdaterings-modalen i Härden-appen. När minsta-version höjs
            tvingas användare med äldre version att uppdatera vid nästa appstart. Höj alltid
            <strong>senaste-version</strong> först, vänta tills användarna har uppdaterat (kan ses
            i användar-aktivitets-vyn), sedan höj <strong>minsta-version</strong>.
          </p>

          <h4 class="card-subhead">Aktuella värden</h4>
          <dl class="walker-summary">
            <div class="walker-row">
              <dt>Minsta-version</dt>
              <dd>kod {minSupported.versionCode} · namn {minSupported.versionName}</dd>
            </div>
            <div class="walker-row">
              <dt>Senaste-version</dt>
              <dd>kod {latest.versionCode} · namn {latest.versionName}</dd>
            </div>
            <div class="walker-row">
              <dt>Download-URL</dt>
              <dd class="break">{latest.downloadUrl}</dd>
            </div>
          </dl>

          <h4 class="card-subhead">Sätt minsta-version</h4>
          <form
            class="walker-form"
            onsubmit={(e) => {
              e.preventDefault();
              void handleSaveMinSupported();
            }}
          >
            <div class="field-row two-col">
              <label class="field">
                <span>Version-kod</span>
                <input
                  type="number"
                  step="1"
                  min="1"
                  bind:value={formMinVersionCode}
                  required
                  disabled={isSavingMin}
                />
              </label>
              <label class="field">
                <span>Version-namn (X.Y.Z)</span>
                <input
                  type="text"
                  pattern="\d+\.\d+\.\d+"
                  bind:value={formMinVersionName}
                  required
                  disabled={isSavingMin}
                />
              </label>
            </div>
            {#if minSaveError}
              <div class="alert error" role="alert">{minSaveError}</div>
            {/if}
            {#if minSaveSuccess}
              <div class="alert success" role="status">
                Minsta-version sparad. Nästa appstart kontrollerar mot detta värde.
              </div>
            {/if}
            <div class="form-actions">
              <button type="submit" class="settings-primary" disabled={isSavingMin}>
                {isSavingMin ? 'Sparar…' : 'Spara minsta-version'}
              </button>
            </div>
          </form>

          <h4 class="card-subhead">Sätt senaste-version</h4>
          <form
            class="walker-form"
            onsubmit={(e) => {
              e.preventDefault();
              void handleSaveLatest();
            }}
          >
            <div class="field-row two-col">
              <label class="field">
                <span>Version-kod</span>
                <input
                  type="number"
                  step="1"
                  min="1"
                  bind:value={formLatestVersionCode}
                  required
                  disabled={isSavingLatest}
                />
              </label>
              <label class="field">
                <span>Version-namn (X.Y.Z)</span>
                <input
                  type="text"
                  pattern="\d+\.\d+\.\d+"
                  bind:value={formLatestVersionName}
                  required
                  disabled={isSavingLatest}
                />
              </label>
            </div>
            <label class="field">
              <span>Download-URL (måste börja med https://harden.compuna.se/apk/)</span>
              <input
                type="url"
                bind:value={formLatestDownloadUrl}
                required
                disabled={isSavingLatest}
              />
            </label>
            {#if latestSaveError}
              <div class="alert error" role="alert">{latestSaveError}</div>
            {/if}
            {#if latestSaveSuccess}
              <div class="alert success" role="status">
                Senaste-version sparad. Apparna visar nu en uppdaterings-snackbar tills de installerat.
              </div>
            {/if}
            <div class="form-actions">
              <button type="submit" class="settings-primary" disabled={isSavingLatest}>
                {isSavingLatest ? 'Sparar…' : 'Spara senaste-version'}
              </button>
            </div>
          </form>
        </div>
      {/if}
    </div>
  </div>
</section>

<style>
  .settings-section {
    display: grid;
    gap: 1rem;
  }
  .settings-layout {
    display: grid;
    grid-template-columns: 180px 1fr;
    gap: 1.5rem;
    align-items: start;
  }
  .section-nav {
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
  }
  .section-btn {
    background: transparent;
    border: 1px solid transparent;
    border-radius: 4px;
    color: var(--text-dim);
    padding: 0.5rem 0.85rem;
    font-size: 0.9rem;
    text-align: left;
    cursor: pointer;
    font-family: inherit;
  }
  .section-btn.active {
    background: var(--accent-soft);
    border-color: var(--accent);
    color: var(--glow);
  }
  .section-btn:hover:not(.active) {
    color: var(--text);
    border-color: var(--rule);
  }
  .section-content {
    display: grid;
    gap: 1rem;
    max-width: 720px;
  }
  .settings-card {
    background: var(--bg-elev);
    border: 1px solid var(--rule);
    border-radius: 6px;
    padding: 1.5rem;
    color: var(--text);
  }
  .settings-card h3 {
    margin: 0 0 0.5rem;
    font-size: 1.05rem;
  }
  .settings-card p {
    margin: 0 0 1rem;
    color: var(--text-dim);
    line-height: 1.55;
  }
  .settings-card p:last-child {
    margin-bottom: 0;
  }
  .settings-primary {
    background: var(--accent);
    color: var(--bg);
    border: 1px solid var(--accent);
    border-radius: 4px;
    padding: 0.6rem 1.1rem;
    font-size: 0.9rem;
    font-weight: 600;
    font-family: inherit;
    cursor: pointer;
    letter-spacing: 0.02em;
  }
  .settings-primary:hover:not(:disabled) {
    background: var(--glow);
    border-color: var(--glow);
  }
  .settings-primary:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
  .settings-secondary {
    background: transparent;
    color: var(--text);
    border: 1px solid var(--rule);
    border-radius: 4px;
    padding: 0.6rem 1.1rem;
    font-size: 0.9rem;
    font-family: inherit;
    cursor: pointer;
  }
  .settings-secondary:hover:not(:disabled) {
    color: var(--glow);
    border-color: var(--text-dim);
  }
  .settings-secondary:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
  .map-form {
    display: grid;
    gap: 0.75rem;
    margin-top: 0.25rem;
  }
  .field-row {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 0.75rem;
  }
  .field {
    display: grid;
    gap: 0.25rem;
    font-size: 0.85rem;
    color: var(--text-dim);
  }
  .field input {
    background: var(--bg);
    border: 1px solid var(--rule);
    border-radius: 4px;
    color: var(--text);
    font-family: inherit;
    font-size: 0.95rem;
    padding: 0.45rem 0.6rem;
  }
  .field input:focus {
    outline: 2px solid var(--accent);
    outline-offset: 1px;
  }
  .form-actions {
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem;
    margin-top: 0.25rem;
  }
  .alert {
    padding: 0.55rem 0.8rem;
    border-radius: 4px;
    font-size: 0.85rem;
  }
  .alert.error {
    background: var(--danger-soft);
    border: 1px solid var(--danger);
    color: var(--text);
  }
  .alert.success {
    background: var(--accent-soft);
    border: 1px solid var(--accent);
    color: var(--text);
  }
  .dim.small {
    font-size: 0.8rem;
    color: var(--text-dim);
  }
  .card-subhead {
    margin: 1.25rem 0 0.4rem;
    font-size: 0.85rem;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    color: var(--text-dim);
  }
  .walker-summary {
    display: grid;
    gap: 0.4rem;
    margin: 0 0 0.5rem;
    padding: 0.75rem 0.9rem;
    border: 1px solid var(--rule);
    border-radius: 4px;
    background: var(--bg);
  }
  .walker-row {
    display: flex;
    gap: 0.75rem;
    flex-wrap: wrap;
    align-items: baseline;
  }
  .walker-row dt {
    color: var(--text-dim);
    font-size: 0.8rem;
    min-width: 8rem;
  }
  .walker-row dd {
    margin: 0;
    color: var(--text);
    font-size: 0.9rem;
    flex: 1;
  }
  .walker-row dd.break {
    word-break: break-all;
    font-family: ui-monospace, monospace;
    font-size: 0.82rem;
  }
  .walker-form {
    display: grid;
    gap: 0.75rem;
    margin-top: 0.25rem;
  }
  .field-row.two-col {
    grid-template-columns: minmax(0, 1fr) minmax(0, 2fr);
  }
  @media (max-width: 720px) {
    .settings-layout {
      grid-template-columns: 1fr;
    }
    .section-nav {
      flex-direction: row;
      flex-wrap: wrap;
    }
    .field-row {
      grid-template-columns: 1fr;
    }
  }
</style>
